import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import "../../styles/Home.css";
import Header from "../../components/Header";
import { GetPodcastByCategoryId } from "../../services/Api";
import { FaPlay, FaPause, FaBackward, FaForward } from "react-icons/fa";

// Utility: convert seconds to mm:ss format
const formatDuration = (durationInSeconds) => {
  const totalSeconds = parseInt(durationInSeconds, 10);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const PodcastDetails = () => {
  const location = useLocation();
  const categoryId = location.state?.categoryId || "1";
  const categoryTitle = location.state?.categoryTitle || "Podcast";
  const categoryImageUrl = location.state?.categoryImageUrl || "";

  const [podcasts, setPodcasts] = useState([]);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);

  useEffect(() => {
    const fetchPodcasts = async () => {
      const res = await GetPodcastByCategoryId(categoryId);
      if (res && res.data) {
        setPodcasts(res.data);
        setCurrentAudio(res.data[0]); // autoplay first item
      }
    };
    fetchPodcasts();
  }, [categoryId]);

  const handleAudioPlay = (audio) => {
    if (audioRef) {
      audioRef.pause();
    }
    setCurrentAudio(audio);
    setIsPlaying(true);
  };

  useEffect(() => {
    if (currentAudio) {
      const audio = new Audio(currentAudio.audioFilePresignedUrl);
      audio.play();
      setAudioRef(audio);

      audio.onended = () => {
        setIsPlaying(false);
      };

      return () => audio.pause();
    }
  }, [currentAudio]);

  const togglePlayback = () => {
    if (!audioRef) return;

    if (isPlaying) {
      audioRef.pause();
      setIsPlaying(false);
    } else {
      audioRef.play();
      setIsPlaying(true);
    }
  };

  return (
    <div className="page-container relative bg-white min-h-screen">
      {/* Header */}
      <Header />

      {/* Top Audio Player */}
      {currentAudio && (
        <div className="w-full max-w-3xl mx-auto bg-cyan-200 rounded-lg shadow-md mt-6 px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Category Image or Placeholder */}
            {categoryImageUrl ? (
              <img
                src={categoryImageUrl}
                alt="Category"
                className="w-24 h-24 rounded-full object-cover bg-white"
              />
            ) : (
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-3xl">
                🎧
              </div>
            )}

            {/* Title and Description */}
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl font-bold text-gray-800">{categoryTitle}</h2>
              <h3 className="text-lg text-gray-700 mt-1">{currentAudio.audioTitle}</h3>
              <p className="text-gray-600">{currentAudio.audioDescription}</p>
            </div>
          </div>

          {/* Audio Controls */}
          <div className="flex justify-center items-center mt-4 gap-6 text-2xl text-gray-700">
            <FaBackward className="cursor-pointer" />
            {isPlaying ? (
              <FaPause className="cursor-pointer" onClick={togglePlayback} />
            ) : (
              <FaPlay className="cursor-pointer" onClick={togglePlayback} />
            )}
            <FaForward className="cursor-pointer" />
          </div>
        </div>
      )}

      {/* Podcast Cards List */}
      <div className="w-full flex flex-col items-center gap-6 mt-10 pb-10">
        {podcasts.map((podcast) => (
          <div
            key={podcast.podcastId}
            className="w-full max-w-3xl bg-gray-50 shadow-md rounded-lg p-4 cursor-pointer hover:shadow-lg transition"
            onClick={() => handleAudioPlay(podcast)}
          >
            <div className="flex items-center gap-4">
              <button className="bg-cyan-500 text-white rounded-full p-3">
                <FaPlay />
              </button>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800 text-base">{podcast.audioTitle}</h3>
                <p className="text-gray-600 text-sm">{podcast.audioDescription}</p>
              </div>
              <span className="text-sm text-gray-400">
                {podcast.audioDuration && podcast.audioDuration !== "0"
                  ? formatDuration(podcast.audioDuration)
                  : "0:00"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PodcastDetails;
